import { useCallback, useEffect, useState } from 'preact/hooks';
import {
  DEFAULT_VAULT_CONFIG,
  loadDashboard,
  parseVaultConfig,
  themePath,
  CONFIG_PATH,
  type LoadedDashboard,
  type VaultConfig,
  type VaultFS,
} from '@hud/core';
import { VaultStore, widgetSchemas } from '@hud/ui';

export type DashboardState =
  | { status: 'loading' }
  | { status: 'ready'; store: VaultStore; loaded: LoadedDashboard; config: VaultConfig }
  | { status: 'failed'; message: string };

export interface UseDashboardResult {
  state: DashboardState;
  reload: () => void;
}

/**
 * Owns the vault store and resolves the current dashboard from it.
 *
 * The store is created once and reused across route changes — reloading the
 * whole vault when you tap a nav link would throw away pending writes and
 * flicker. Only the dashboard resolution re-runs.
 */
export function useDashboard(vault: VaultFS, name: string): UseDashboardResult {
  const [store] = useState(() => new VaultStore(vault));
  const [state, setState] = useState<DashboardState>({ status: 'loading' });
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      await store.load();
      // The store already holds every text file, so config costs no extra fetch.
      const { config } = parseVaultConfig(store.get(CONFIG_PATH));
      const loaded = await loadDashboard(vault, name, widgetSchemas);
      if (cancelled) return;
      setState({ status: 'ready', store, loaded, config: config ?? DEFAULT_VAULT_CONFIG });
      void applyTheme(vault, loaded.dashboard.theme || config.theme);
    })().catch((err: unknown) => {
      if (cancelled) return;
      setState({ status: 'failed', message: err instanceof Error ? err.message : String(err) });
    });

    return () => {
      cancelled = true;
    };
  }, [vault, store, name, nonce]);

  // External edits: the dev server pushes a path on every disk write, so
  // editing markdown in your editor updates the HUD without a page reload.
  // Only the changed file is re-read, and queued writes replay on top of it.
  useEffect(() => {
    if (!import.meta.hot) return;

    const onChange = (data: { path?: string }) => {
      if (!data.path) return;
      void store.refresh(data.path);
      // Layout lives in the dashboard file, so a change there needs re-resolving.
      if (data.path.startsWith('dashboards/') || data.path.startsWith('widgets/')) reload();
    };

    import.meta.hot.on('hud:vault-changed', onChange);
    return () => import.meta.hot?.off('hud:vault-changed', onChange);
  }, [store, reload]);

  // Never lose a pending write to a closed tab.
  useEffect(() => {
    const flush = () => void store.flush();
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush();
    });
    return () => window.removeEventListener('pagehide', flush);
  }, [store]);

  return { state, reload };
}

const THEME_STYLE_ID = 'hud-theme';

/**
 * Inject the vault's theme CSS. A theme is a plain `.css` file overriding the
 * design tokens, so a missing one is not an error — the built-in defaults just
 * stay in effect.
 */
async function applyTheme(vault: VaultFS, theme: string): Promise<void> {
  let css = '';
  try {
    css = (await vault.read(themePath(theme))).content;
  } catch {
    css = '';
  }

  let style = document.getElementById(THEME_STYLE_ID);
  if (!style) {
    style = document.createElement('style');
    style.id = THEME_STYLE_ID;
    document.head.append(style);
  }
  style.textContent = css;
}
